import {useCallback} from 'react';

import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import DocumentStore from 'notebook/documents/DocumentStore';
import {InitialCellValue} from 'notebook/structured-tables/util/getInitialCellValueFromLiveValue';
import type {Cell} from 'notebook/tables/constants/CellConstants';
import {type InsertableColumnInfo} from 'notebook/tables/constants/TableColumnConstants';
import {MultiSelectionBox, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {type TableOperation} from 'notebook/tables/excess/ops/TableOpConstants';
import {ReorderOptions} from 'notebook/tables/excess/ops/TableOps';
import {useTableOps} from 'notebook/tables/spreadsheets/TableOpContext';
import {TableModelContext} from 'notebook/tables/store/contexts/TableModelContext';
import {useTableModel} from 'notebook/tables/store/hooks/useTableModel';
import {clearCellsInRange as clearCellsInRangeMutation} from 'notebook/tables/store/logic/mutations/clearCellsInRange';
import {
  toggleHeaderCol as toggleHeaderColMutation,
  toggleHeaderRow as toggleHeaderRowMutation,
} from 'notebook/tables/store/logic/mutations/toggleHeader';
import {updateCell as updateCellMutation} from 'notebook/tables/store/logic/mutations/updateCell';
import {computeMergeValidation, type MergeValidation} from 'notebook/tables/store/logic/TableMergingLogic';
import TableUtil from 'notebook/tables/TableUtil';
import {
  trackCrossStepReferenceChange,
  trackCrossStepReferenceBulkDelete,
} from 'notebook/tables/util/analytics/CrossStepReferenceAnalytics';
import {getWorksheetIdFromNamespace} from 'notebook/tables/util/WorksheetTableNamespaceUtil';
import {trackContainerValidityForTableEvents} from 'samples/containers/ValidContentsUtils';

interface TableOpsMethods {
  renameTable: (tableName: string, newTableName: string) => void;
  performTableOp?: (tableOp: TableOperation) => void;
  deleteColumns: (tableName: string, start: number, end: number) => void;
  insertColumnsLeft: (
    tableName: string,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {},
    initialCellValues?: Array<InitialCellValue>
  ) => void;
  insertColumnsRight: (
    tableName: string,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {},
    initialCellValues?: Array<InitialCellValue>
  ) => void;
  deleteRows: (tableName: string, start: number, end: number) => void;
  deleteRowsNonContiguous: (tableName: string, ranges: Array<[number, number]>) => void;
  insertRowsAbove: (tableName: string, start: number, end: number) => void;
  insertRowsBelow: (tableName: string, start: number, end: number) => void;
  mergeRegion: ((tableName: string, selectedBox: MultiSelectionBox) => void) | null;
  unmergeRegion: ((tableName: string, selectedBox: MultiSelectionBox) => void) | null;
  reorderRowsOrColumns: (tableName: string, options: ReorderOptions) => void;
}

export interface TableMutationMethods extends TableOpsMethods {
  /**
   * Update a single cell in the table.
   *
   * @param cellIndex - The [row, column] index of the cell to update
   * @param cellData - Partial cell data to merge into the existing cell
   * @param expandTable - If true, add a new blank row at the bottom
   */
  updateCell: (cellIndex: TableIndex, cellData: Partial<Cell>, expandTable?: boolean) => void;

  /**
   * Clear all cells within a selection box.
   *
   * @param selectionBox - The box defining which cells to clear
   */
  clearCellsInRange: (selectionBox: MultiSelectionBox) => void;

  /**
   * Clear all cells within the current selection, expanding to fully contain any
   * intersecting merged regions. No-op when there is no active selection.
   */
  clearSelection: () => void;

  /**
   * Toggle the header column flag on the table, clearing bold formatting on the first column.
   */
  toggleHeaderCol: () => void;

  /**
   * Toggle the header row flag on the table, clearing bold formatting on the first row.
   */
  toggleHeaderRow: () => void;

  /**
   * Validate whether the current selection can be merged into a single cell.
   *
   * Reads the merge-expanded selection box from the model and reports whether the
   * selection straddles the freeze boundary (merging disallowed) and whether the
   * merge would discard cell values (caller should confirm). Returns `null` when
   * there is no active selection.
   *
   * The merge itself is performed separately via `mergeRegion`; the confirmation
   * modal is owned by the component.
   */
  validateMerge: () => MergeValidationResult | null;
}

/**
 * The result of validating a merge of the current selection, including the
 * merge-expanded box that should be passed to `mergeRegion`.
 */
export interface MergeValidationResult extends MergeValidation {
  /** The merge-expanded selection box to merge. */
  box: MultiSelectionBox;
}

/**
 * Hook that provides table mutation methods with analytics and persistence.
 *
 * All data is fetched fresh from the TableModel at execution time,
 * ensuring we always use the latest state and keeping callbacks stable.
 */
export function useTableMutations(): TableMutationMethods {
  const {useTableEditableStore, tableModelSelector} = TableModelContext.useContext();
  const tableOps = useTableOps();
  const {tableModel: disableMergingCells} = useTableModel((state) => state.props.disableMergingCells);

  const getTableModel = useCallback(
    () => tableModelSelector(useTableEditableStore.getState()),
    [useTableEditableStore, tableModelSelector]
  );

  const updateCell = useCallback(
    (cellIndex: TableIndex, cellData: Partial<Cell>, expandTable?: boolean): void => {
      const model = getTableModel();
      const {table, index, fileId, tableNamespace, updateHandler} = model.props;
      const {boundaryIndex: singleInputBoundaryIndex} = model.singleInputs;

      // Execute pure mutation
      const result = updateCellMutation(table, cellIndex, cellData, {
        expandTable,
        singleInputBoundaryIndex,
      });

      // Track cross-step reference analytics
      if (cellData.text != null) {
        const oldCellText = result.affectedCells[0]?.oldCell?.text ?? '';
        const worksheetId = getWorksheetIdFromNamespace(tableNamespace);
        const worksheetInfo = worksheetId
          ? {worksheetId, stepCount: DocumentStore.getRelatedDocumentIds(fileId).length}
          : null;
        trackCrossStepReferenceChange(oldCellText, cellData.text, worksheetInfo);
      }

      // Persist the change
      updateHandler(
        index,
        {table: result.table},
        undefined,
        result.tableOp != null ? {[table.name]: result.tableOp} : undefined
      );

      // Track container validity analytics
      const newCell = result.affectedCells[0]?.newCell;
      if (newCell) {
        trackContainerValidityForTableEvents(fileId, table.apiId!, [
          {event: ELN_ANALYTICS_ACTIONS.ADDED_CONTAINERS_TO_STRUCTURED_TABLE, cells: [newCell]},
        ]);
      }
    },
    [getTableModel]
  );

  const clearCellsInRange = useCallback(
    (selectionBox: MultiSelectionBox): void => {
      const model = getTableModel();
      const {table, index, fileId, tableNamespace, updateHandler} = model.props;

      // Execute pure mutation
      const result = clearCellsInRangeMutation(table, selectionBox);

      // Track cross-step reference analytics
      const oldCells = result.affectedCells.map((c) => c.oldCell).filter((c): c is Cell => c != null);
      const worksheetId = getWorksheetIdFromNamespace(tableNamespace);
      const worksheetInfo = worksheetId
        ? {worksheetId, stepCount: DocumentStore.getRelatedDocumentIds(fileId).length}
        : null;
      trackCrossStepReferenceBulkDelete(oldCells, worksheetInfo);

      // Persist the change
      updateHandler(
        index,
        {table: result.table},
        undefined,
        result.tableOp != null ? {[table.name]: result.tableOp} : undefined
      );

      // Track container validity analytics
      if (oldCells.length > 0) {
        trackContainerValidityForTableEvents(fileId, table.apiId!, [
          {
            event: ELN_ANALYTICS_ACTIONS.REMOVED_CONTAINERS_FROM_STRUCTURED_TABLE,
            cells: oldCells,
          },
        ]);
      }
    },
    [getTableModel]
  );

  const clearSelection = useCallback((): void => {
    const model = getTableModel();
    const {expandedBox} = model.selection;

    // Nothing to clear when there is no active selection.
    if (expandedBox == null) {
      return;
    }

    clearCellsInRange(expandedBox);
  }, [getTableModel, clearCellsInRange]);

  const toggleHeaderCol = useCallback((): void => {
    const model = getTableModel();
    const {table, index, updateHandler} = model.props;

    const result = toggleHeaderColMutation(table);

    updateHandler(
      index,
      {table: result.table},
      undefined,
      result.tableOp != null ? {[table.name]: result.tableOp} : undefined
    );
  }, [getTableModel]);

  const toggleHeaderRow = useCallback((): void => {
    const model = getTableModel();
    const {table, index, updateHandler} = model.props;

    const result = toggleHeaderRowMutation(table);

    updateHandler(
      index,
      {table: result.table},
      undefined,
      result.tableOp != null ? {[table.name]: result.tableOp} : undefined
    );
  }, [getTableModel]);

  const validateMerge = useCallback((): MergeValidationResult | null => {
    const model = getTableModel();
    const {expandedBox} = model.selection;

    // Nothing to validate when there is no active selection.
    if (expandedBox == null) {
      return null;
    }

    const {table} = model.props;
    const freezeBoundary = TableUtil.getFreezeBoundary(table);
    const validation = computeMergeValidation(table.table, expandedBox, freezeBoundary);

    return {box: expandedBox, ...validation};
  }, [getTableModel]);

  return {
    updateCell,
    clearCellsInRange,
    clearSelection,
    toggleHeaderCol,
    toggleHeaderRow,
    validateMerge,
    renameTable: tableOps.renameTable,
    performTableOp: tableOps.performTableOp,
    deleteColumns: tableOps.deleteTableColumns,
    insertColumnsLeft: tableOps.insertTableColumnsLeft,
    insertColumnsRight: tableOps.insertTableColumnsRight,
    deleteRows: tableOps.deleteTableRows,
    deleteRowsNonContiguous: tableOps.deleteTableRowsNonContiguous,
    insertRowsAbove: tableOps.insertTableRowsAbove,
    insertRowsBelow: tableOps.insertTableRowsBelow,
    mergeRegion: disableMergingCells ? null : tableOps.mergeTableRegion,
    unmergeRegion: disableMergingCells ? null : tableOps.unmergeTableRegion,
    reorderRowsOrColumns: tableOps.reorderTableRowsOrColumns,
  };
}
